<script lang="ts">
    import type {Loan} from 'typings/portaro.be.types';
    import type {PaginationData} from '../../search-manager/pagination';
    import {LoanState} from 'typings/portaro.be.types';
    import {getDateFormatter, getInjector, getLocalization} from 'core/svelte-context/context';
    import KpSearchResultGenericTable from '../KpSearchResultGenericTable.svelte';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import {fade} from 'svelte/transition';
    import KpActionRequestButton from 'shared/components/kp-action-request-button/KpActionRequestButton.svelte';
    import Label from 'shared/components/kp-label/Label.svelte';
    import {Kind} from 'shared/constants/portaro.constants';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import {SearchedLoansService} from './searched-loans-service';

    export let loansPaginationData: PaginationData<Loan>;

    const localize = getLocalization();
    const dateFormatter = getDateFormatter();
    const service = getInjector().getByClass(SearchedLoansService);

    function isProcessedOrderOrSentReservation(loan: Loan): boolean {
        return [LoanState.PROCESSED_ORDER, LoanState.SENT_RESERVATION].includes(loan.state.id as LoanState);
    }
</script>

<KpSearchResultGenericTable>
    <tr slot="header">
        <th class="header-id">#</th>
        <th class="header-document-name">{localize(/* @kp-localization commons.nazev */ 'commons.nazev')}</th>
        <th class="header-lender">{localize(/* @kp-localization loan.Lender */ 'loan.Lender')}</th>
        <th class="header-exemplar-name">{localize(/* @kp-localization exemplar.Exemplar.abbr */ 'exemplar.Exemplar.abbr')}</th>
        <th class="header-state">{localize(/* @kp-localization commons.stav */ 'commons.stav')}</th>
        <th class="header-department">{localize(/* @kp-localization department.Department.abbr */ 'department.Department.abbr')}</th>
        <th class="header-desired-department">{localize(/* @kp-localization loan.DesiredDepartment.abbr */ 'loan.DesiredDepartment.abbr')}</th>
        <th class="header-lend-date">{localize(/* @kp-localization loan.LoanDate.abbr */ 'loan.LoanDate.abbr')}</th>
        <th class="header-end-date">{localize(/* @kp-localization loan.EndDate.abbr */ 'loan.EndDate.abbr')}</th>
        <th class="header-delete-or-more">{localize(/* @kp-localization commons.Options */ 'commons.Options')}</th>
    </tr>

    <svelte:fragment slot="body">
        {#each loansPaginationData.items as loan(loan.id)}
            <tr in:fade>
                <td class="data-id">{loan.loanRealizationId}</td>

                <td class="data-document-name">
                    <Label explicitKind="{Kind.KIND_RECORD}" labeled="{loan.document}"/>
                </td>

                <td class="data-lender">
                    <Label explicitKind="{Kind.KIND_USER}" labeled="{loan.user}"/>
                </td>

                <td class="data-exemplar-name">
                    {#if loan.exemplar?.accessNumber}
                        <div>
                            <small class="text-muted">{localize(/* @kp-localization exemplar.AccessNumber.abbr */ 'exemplar.AccessNumber.abbr')}</small>
                            <small>{loan.exemplar.accessNumber}</small>
                        </div>
                    {/if}

                    {#if loan.exemplar?.barCode}
                        <div>
                            <small class="text-muted">{localize(/* @kp-localization exemplar.BarCode.abbr */ 'exemplar.BarCode.abbr')}</small>
                            <small>{loan.exemplar.barCode}</small>
                        </div>
                    {/if}

                    {#if loan.exemplar?.signature}
                        <div>
                            <small class="text-muted">{localize(/* @kp-localization exemplar.Signature.abbr */ 'exemplar.Signature.abbr')}</small>
                            <small>{loan.exemplar.signature}</small>
                        </div>
                    {/if}
                </td>

                <td class="data-state">{pipe(loan.state, loc())}</td>

                <td class="data-department">{pipe(loan.department, loc())}</td>

                <td class="data-desired-department">
                    {#each loan.desiredDepartments as desiredDepartment(desiredDepartment.id)}
                        <div>{pipe(desiredDepartment, loc())}</div>
                    {/each}
                </td>

                <td class="data-lend-date">
                    {#if loan.lendDate}
                        {pipe(loan.lendDate, dateFormatter('d.M.yyyy'))}
                    {/if}
                </td>

                <td class="data-end-date">
                    {#if loan.endDate}
                        {pipe(loan.endDate, dateFormatter('d.M.yyyy'))}
                    {/if}
                </td>

                <td class="data-delete-or-more">
                    <div class="action-buttons-container">
                        <KpButton isBlock
                                  buttonSize="sm"
                                  on:click={() => service.cancelLoan(loan)}>
                            <IconedContent icon="trash">
                                {localize(/* @kp-localization commons.Smazat */ 'commons.Smazat')}
                            </IconedContent>
                        </KpButton>

                        {#if isProcessedOrderOrSentReservation(loan)}
                            <KpActionRequestButton isBlock
                                                   buttonSize="sm"
                                                   path="/api/loan-items/{loan.id}/notify">
                                <IconedContent icon="refresh">
                                    Znovu odeslat upozornění
                                </IconedContent>
                            </KpActionRequestButton>
                        {/if}
                    </div>
                </td>
            </tr>
        {/each}
    </svelte:fragment>
</KpSearchResultGenericTable>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .action-buttons-container {
        display: flex;
        flex-direction: column;
        gap: @padding-xs-vertical;
    }
</style>